import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { fmtSpeed, isMarine } from "@/lib/fleet";
import { X, LocateFixed, Navigation2, ArrowUpRight, ArrowUp, CornerUpRight, CornerUpLeft, RotateCcw, Flag, AlertTriangle } from "lucide-react";

// LT1.4b — Mode navigation « Rejoindre le véhicule ». Composant AUTO-CONTENU :
// watchPosition navigateur (aucun polling GPS maison), destination = véhicule mobile lu dans la flotte déjà chargée
// (delta polling existant), recalcul /route throttlé >= 15 s. N'utilise PAS followId (mode Suivre indépendant).

const THROTTLE_MS = 15000;      // jamais plus d'1 GET /route toutes les 15 s
const TARGET_MOVE_M = 150;      // recalcul si la cible s'est déplacée >= 150 m
const OFFROUTE_M = 75;          // recalcul si l'utilisateur s'écarte >= 75 m de la route
const ROUTE_AGE_MS = 30000;     // route âgée >= 30 s (+ cible déplacée)
const ARRIVE_M = 50;            // « Véhicule rejoint »
const OFFLINE_MS = 3 * 60000;   // cible non actualisée depuis 3 min

const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
function haversineM(aLat, aLon, bLat, bLon) {
  const dLat = rad(bLat - aLat), dLon = rad(bLon - aLon);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}
// Distance point -> polyline (mètres), O(segments), calculée uniquement sur nouvelle position GPS.
function distToPolylineM(lat, lon, path) {
  if (!path || path.length === 0) return Infinity;
  if (path.length === 1) return haversineM(lat, lon, path[0].lat, path[0].lon);
  let best = Infinity;
  const latScale = 111320, lonScale = 111320 * Math.cos(rad(lat));
  const px = lon * lonScale, py = lat * latScale;
  for (let i = 0; i < path.length - 1; i++) {
    const ax = path[i].lon * lonScale, ay = path[i].lat * latScale;
    const bx = path[i + 1].lon * lonScale, by = path[i + 1].lat * latScale;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy || 1;
    let t = ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx, cy = ay + t * dy;
    const d = Math.hypot(px - cx, py - cy);
    if (d < best) best = d;
  }
  return best;
}

// Turn-by-turn FR : mapping borné maneuver_type/modifier -> texte + icône. Aucune invention si type inconnu.
const MOD_FR = {
  left: "à gauche", right: "à droite", "slight left": "légèrement à gauche", "slight right": "légèrement à droite",
  "sharp left": "franchement à gauche", "sharp right": "franchement à droite", straight: "tout droit",
  uturn: "demi-tour",
};
function maneuverText(step) {
  if (!step) return { text: "", Icon: Navigation2 };
  const t = step.maneuver_type, m = step.maneuver_modifier, name = step.name;
  const on = name ? ` sur ${name}` : "";
  if (t === "turn") return { text: `Tournez ${MOD_FR[m] || ""}${on}`.trim(), Icon: m && m.includes("left") ? CornerUpLeft : CornerUpRight };
  if (t === "roundabout" || t === "rotary") return { text: `Au rond-point, prenez la sortie${on}`, Icon: RotateCcw };
  if (t === "merge") return { text: `Insérez-vous${on}`, Icon: ArrowUpRight };
  if (t === "fork") return { text: `Restez ${MOD_FR[m] || "tout droit"}${on}`, Icon: ArrowUpRight };
  if (t === "on ramp" || t === "off ramp") return { text: `Prenez la bretelle${on}`, Icon: ArrowUpRight };
  if (t === "continue" || t === "new name") return { text: `Continuez${on}`, Icon: ArrowUp };
  if (t === "arrive") return { text: `Vous êtes arrivé${on}`, Icon: Flag };
  if (t === "depart") return { text: `Départ${on}`, Icon: ArrowUp };
  return { text: name ? `Continuez sur ${name}` : "Continuez", Icon: ArrowUp };
}

function NavCamera({ userPos, recenterKey, path, target }) {
  const map = useMap();
  const started = useRef(false);
  useEffect(() => {
    if (!userPos) return;
    if (!started.current) {
      started.current = true;
      map.setView([userPos.lat, userPos.lon], Math.max(map.getZoom(), 16), { animate: true });
    } else {
      map.panTo([userPos.lat, userPos.lon], { animate: true, duration: 1.4 });
    }
  }, [userPos, map]);
  // Recentrer : englobe utilisateur + cible + route utile.
  useEffect(() => {
    if (!recenterKey) return;
    const pts = [];
    if (userPos) pts.push([userPos.lat, userPos.lon]);
    if (target) pts.push([target.lat, target.lon]);
    (path || []).forEach((p) => pts.push([p.lat, p.lon]));
    if (pts.length === 1) map.setView(pts[0], 16, { animate: true });
    else if (pts.length > 1) map.fitBounds(L.latLngBounds(pts), { padding: [60, 60], maxZoom: 16 });
  }, [recenterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const userIcon = () => L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 2px #2563EB55"></div>` });
const targetIcon = () => L.divIcon({ className: "", iconSize: [30, 30], iconAnchor: [15, 30],
  html: `<div style="font-size:26px;line-height:1;filter:drop-shadow(0 2px 2px #0006)">📍</div>` });

export default function NavigationOverlay({ vehicle, vehicles = [], onExit }) {
  const [userPos, setUserPos] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [route, setRoute] = useState(null);   // {geometry, distance_km, duration_min, steps, targetUsed, at}
  const [recenterKey, setRecenterKey] = useState(0);
  const [arrived, setArrived] = useState(false);
  const [routeError, setRouteError] = useState(false);
  const watchRef = useRef(null);
  const lastRouteAtRef = useRef(0);
  const busyRef = useRef(false);
  const vehicleId = vehicle?.id;

  // Cible mobile : lue dans la flotte déjà chargée (delta polling existant), aucune requête supplémentaire.
  const live = vehicles.find((v) => v.id === vehicleId) || vehicle;
  const lp = live?.last_position || {};
  const target = (lp.lat != null && lp.lon != null) ? { lat: lp.lat, lon: lp.lon } : null;
  const lastTs = lp.timestamp ? new Date(lp.timestamp).getTime() : null;
  const offline = lastTs != null && (Date.now() - lastTs) > OFFLINE_MS;

  const computeRoute = useCallback(async (uPos, tPos) => {
    if (!uPos || !tPos || busyRef.current) return;
    if (Date.now() - lastRouteAtRef.current < THROTTLE_MS) return;  // throttle dur
    busyRef.current = true;
    lastRouteAtRef.current = Date.now();
    try {
      const r = await api.get(`/route?from_lat=${uPos.lat}&from_lon=${uPos.lon}&to_lat=${tPos.lat}&to_lon=${tPos.lon}`);
      const geometry = (r.data.geometry || []).map(([lat, lon]) => ({ lat, lon }));
      setRoute({ geometry, distance_km: r.data.distance_km, duration_min: r.data.duration_min,
                 steps: r.data.steps || [], targetUsed: tPos, at: Date.now() });
      setRouteError(false);
    } catch {
      // Panne OSRM (timeout/429/500/vide) : conserver la dernière route valide, signaler, PAS de retry agressif
      // (le throttle 15 s empêche toute boucle ; la prochaine tentative aura lieu au prochain événement éligible).
      setRouteError(true);
    }
    finally { busyRef.current = false; }
  }, []);

  // watchPosition — démarré à l'entrée du mode, nettoyé à la sortie / changement de véhicule / démontage.
  useEffect(() => {
    if (!navigator.geolocation) { setGpsError("no-geo"); return; }
    setUserPos(null); setRoute(null); setArrived(false); lastRouteAtRef.current = 0;
    const id = navigator.geolocation.watchPosition(
      (p) => { setGpsError(null); setUserPos({ lat: p.coords.latitude, lon: p.coords.longitude,
        accuracy: p.coords.accuracy, heading: p.coords.heading, speed: p.coords.speed, ts: p.timestamp }); },
      (err) => { setGpsError(err.code === 1 ? "denied" : "unavailable"); },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 }
    );
    watchRef.current = id;
    return () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [vehicleId]);

  // Décision de recalcul : uniquement sur nouvelle position GPS ou nouvelle position cible (pas à chaque render).
  useEffect(() => {
    if (!userPos || !target || offline) return;
    const first = !route;
    let need = first;
    if (!need && route.targetUsed) {
      const targMoved = haversineM(route.targetUsed.lat, route.targetUsed.lon, target.lat, target.lon);
      const off = distToPolylineM(userPos.lat, userPos.lon, route.geometry);
      const aged = Date.now() - route.at >= ROUTE_AGE_MS;
      if (targMoved >= TARGET_MOVE_M) need = true;                 // B
      else if (off >= OFFROUTE_M) need = true;                     // C
      else if (aged && targMoved >= 40) need = true;               // D
    }
    // Arrivée (indépendant du recalcul) : distance directe utilisateur -> cible.
    setArrived(haversineM(userPos.lat, userPos.lon, target.lat, target.lon) < ARRIVE_M);
    if (need) computeRoute(userPos, target);
  }, [userPos, target?.lat, target?.lon, offline]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retour d'arrière-plan : réévaluer sans créer de second watcher (watchPosition reste unique).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible" && userPos && target && !offline) computeRoute(userPos, target); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [userPos, target?.lat, target?.lon, offline]); // eslint-disable-line react-hooks/exhaustive-deps

  const moving = !offline && (lp.speed || 0) > 3;
  const nextStep = route?.steps?.find((s) => (s.maneuver_type !== "depart")) || route?.steps?.[0];
  const man = maneuverText(nextStep);
  const NextIcon = man.Icon;
  const center = userPos ? [userPos.lat, userPos.lon] : (target ? [target.lat, target.lon] : [-22.27, 166.44]);

  return (
    <div className="fixed inset-0 z-[900] bg-background" data-testid="navigation-mode">
      <MapContainer center={center} zoom={16} zoomControl={false} attributionControl={false} className="w-full h-full">
        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}" attribution="© Esri" />
        {route?.geometry?.length > 1 && (
          <Polyline positions={route.geometry.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#2ec5db", weight: 6, opacity: 0.9 }} />
        )}
        {target && <Marker position={[target.lat, target.lon]} icon={targetIcon()} />}
        {userPos && (
          <>
            <CircleMarker center={[userPos.lat, userPos.lon]} radius={2} pathOptions={{ opacity: 0 }} />
            <Marker position={[userPos.lat, userPos.lon]} icon={userIcon()} />
          </>
        )}
        <NavCamera userPos={userPos} recenterKey={recenterKey} path={route?.geometry} target={target} />
      </MapContainer>

      {/* Top : identité cible */}
      <div className="absolute top-3 left-3 right-3 z-[910] flex items-center justify-between gap-2">
        <div className="glass rounded-xl px-3 py-2 min-w-0" data-testid="nav-target">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold text-sm truncate">{live?.name}</span>
            <span className="data text-[11px] text-muted-foreground shrink-0">{live?.plate}</span>
          </div>
          <div className="text-[11px] mt-0.5 flex items-center gap-1.5">
            {offline ? (
              <span className="text-[#F5821F] font-semibold flex items-center gap-1" data-testid="nav-offline">
                <AlertTriangle className="w-3.5 h-3.5" /> Position non actualisée depuis {lastTs ? Math.round((Date.now() - lastTs) / 60000) : "?"} min
              </span>
            ) : (
              <span className="font-semibold" style={{ color: moving ? "#10B981" : "#94a3b8" }}>
                {moving ? "En mouvement" : "À l'arrêt"} · {fmtSpeed(lp.speed, isMarine(live))}
              </span>
            )}
          </div>
        </div>
        <button onClick={onExit} data-testid="nav-quit-top" title="Quitter"
          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full glass text-foreground hover:bg-destructive hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Erreur GPS */}
      {gpsError && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[910] glass rounded-xl px-4 py-3 text-center max-w-[320px]" data-testid="nav-gps-error">
          <p className="text-sm font-semibold text-[#F5821F] flex items-center justify-center gap-1.5"><AlertTriangle className="w-4 h-4" />
            {gpsError === "denied" ? "Localisation refusée" : gpsError === "no-geo" ? "GPS indisponible" : "Signal GPS indisponible"}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Autorisez la localisation puis rouvrez la navigation.</p>
          <button onClick={onExit} data-testid="nav-quit-error" className="mt-2 text-xs font-bold text-[#2563EB] hover:underline">Fermer</button>
        </div>
      )}

      {/* Arrivée */}
      {arrived && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[920] glass rounded-xl px-4 py-3 text-center animate-slidein" data-testid="nav-arrived">
          <p className="text-sm font-bold text-[#10B981] flex items-center justify-center gap-1.5"><Flag className="w-4 h-4" /> Véhicule rejoint</p>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={onExit} data-testid="nav-arrived-finish" className="flex-1 bg-[#10B981] hover:bg-[#0ea371] text-white text-xs font-bold py-1.5 rounded-md">Terminer</button>
            <button onClick={() => setArrived(false)} data-testid="nav-arrived-continue" className="flex-1 border border-border text-xs font-bold py-1.5 rounded-md hover:border-[#2563EB]">Continuer le suivi</button>
          </div>
        </div>
      )}

      {/* Bottom : overlay navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-[910] p-3">
        <div className="glass rounded-2xl px-4 py-3 max-w-[560px] mx-auto" data-testid="nav-overlay">
          {route?.steps?.length > 0 && nextStep && !offline && (
            <div className="flex items-center gap-3 pb-2.5 mb-2.5 border-b border-border/60" data-testid="nav-next-maneuver">
              <div className="w-11 h-11 rounded-xl bg-[#2563EB] text-white flex items-center justify-center shrink-0">
                <NextIcon className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold leading-tight truncate">{man.text}</p>
                {nextStep.distance_m != null && <p className="text-[11px] text-muted-foreground">Dans {nextStep.distance_m >= 1000 ? `${(nextStep.distance_m / 1000).toFixed(1)} km` : `${Math.round(nextStep.distance_m)} m`}</p>}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="data" data-testid="nav-eta">
              {route ? (
                <span className="text-sm font-bold"><span className="text-[#2ec5db]">{route.duration_min} min</span> · {route.distance_km} km</span>
              ) : routeError ? (
                <span className="text-xs font-semibold text-[#F5821F]" data-testid="nav-route-error">Itinéraire indisponible</span>
              ) : (
                <span className="text-xs text-muted-foreground">{userPos ? "Calcul de l'itinéraire…" : "Recherche du signal GPS…"}</span>
              )}
              {route && routeError && (
                <span className="block text-[10.5px] text-[#F5821F] font-semibold" data-testid="nav-recalc-error">Recalcul de l'itinéraire indisponible</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setRecenterKey((k) => k + 1)} data-testid="nav-recenter"
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-semibold border border-border hover:border-[#2563EB]"><LocateFixed className="w-4 h-4" /> Recentrer</button>
              <button onClick={onExit} data-testid="nav-quit"
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-bold bg-destructive text-white hover:bg-red-600"><X className="w-4 h-4" /> Quitter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
